/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import { ImgSanity } from '@slite/components'

import { Button } from '../../../components/Button/Button'
import { QuoteWithArrows } from '../../../components/QuoteWithArrows'
import type { Quote } from '../../../components/SectionQuotes'
import { mediaQueryMedium } from '../../../components/style'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'

type SectionSolutionsAdvantageProps = {
  index: number
  name: string
  title: string
  description: string
  imageUrl: string
  quote: Quote | null
  brandColor: string
  /** Overrides the default "Book a demo" CTA. */
  cta?: { href: string; label: React.ReactNode }
  imagePosition?: 'left' | 'right'
}

export function SectionSolutionsAdvantage({
  index,
  name,
  title,
  description,
  imageUrl,
  quote,
  brandColor,
  cta,
  imagePosition = 'right',
}: SectionSolutionsAdvantageProps) {
  const routes = useRoutes()
  const intl = useIntl()
  const content = (
    <div
      className="flexbox flexbox-column flexbox-align-start flexbox-space-between"
      css={{
        padding: 64,
        paddingRight: 120,
        width: '50%',
        [mediaQueryMedium]: {
          width: '100%',
          padding: 32,
        },
      }}
    >
      <div>
        <WebsiteText
          variant="body"
          className={classNames('shade-10', brandColor)}
        >
          {name}
        </WebsiteText>
        <WebsiteText variant="title" className="padding-top-24 shade-05">
          {title}
        </WebsiteText>
      </div>
      <div>
        <WebsiteText variant="body-lg" className="padding-top-24">
          {description}
        </WebsiteText>
        <div className="padding-top-24">
          <Button variant="secondary" href={cta?.href ?? routes.bookDemo()}>
            {cta?.label ?? (
              <FormattedMessage
                id="solutions.book-demo"
                defaultMessage="Book a demo"
              />
            )}
          </Button>
        </div>
      </div>
    </div>
  )

  const image = (
    <ImgSanity
      css={{
        height: '100%',
        width: '50%',
        objectFit: 'cover',
        [mediaQueryMedium]: {
          height: 'auto',
          width: '100%',
        },
      }}
      src={imageUrl}
      alt={intl.formatMessage(
        {
          id: 'solutions.advantage-illustration',
          defaultMessage: 'Illustration for {name}',
        },
        { name }
      )}
    />
  )

  return (
    <div>
      <div
        className="background-full-white flexbox flexbox-space-between overflow-hidden"
        css={{
          height: 640,
          flexDirection: imagePosition === 'left' ? 'row-reverse' : 'row',
          borderRadius: 32,
          border: '1px solid rgba(0, 0, 0, 0.05)',
          [mediaQueryMedium]: {
            flexDirection: 'column',
            height: 'auto',
          },
        }}
      >
        {content}
        {image}
      </div>
      {quote && (
        <QuoteWithArrows
          quote={quote}
          brandColor={brandColor}
          index={index}
          className="margin-top-16"
        />
      )}
    </div>
  )
}
